import { Suspense, useEffect, useState } from 'react';
import { canAnywhere } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { useAddTeamModal } from './activation/AddTeamModalProvider.js';
// Lazy, like the shell's: Home is in the app's first load, the grid and file reader are not.
import { LazyAddYourTeamModal } from './activation/lazyAddYourTeamModal.js';

function guardKey(orgId: string) {
  return `peopleorbit_welcome_redirected_${orgId}`;
}

/**
 * True when this browser session already sent the owner to the welcome setup. When storage cannot
 * be read it answers true, so a blocked storage can never turn into a redirect loop.
 */
export function welcomeAlreadyShown(orgId: string): boolean {
  try {
    return sessionStorage.getItem(guardKey(orgId)) === '1';
  } catch {
    return true;
  }
}

/** Remembers, for this browser session, that the welcome setup was shown. */
export function markWelcomeShown(orgId: string) {
  try {
    sessionStorage.setItem(guardKey(orgId), '1');
  } catch {
    // Storage unavailable: welcomeAlreadyShown already answers true, so nothing repeats.
  }
}

/**
 * Opens the welcome "Add your team" modal on Home for a new company's owner (FR-4). It used to
 * redirect to the full-page /welcome, which stays reachable. Only for someone who can change the
 * organisation's settings, only while the API says `showWelcome`, and at most once per browser
 * session, so closing the modal or a failed "finish" never brings it straight back. Inside the
 * app shell it opens the shell's one modal (AddTeamModalProvider), the same one Home's activation
 * card and the "Add your first 3 staff" bar open; on its own it renders its own. Renders nothing
 * until it opens.
 */
export function WelcomeRedirect() {
  const { actor } = useAuthenticatedSession();
  const allowed = canAnywhere(actor, 'org.settings:update');
  const [open, setOpen] = useState(false);
  const shared = useAddTeamModal();
  const status = tsr.setupStatus.getSetupStatus.useQuery({
    queryKey: ['setup-status'],
    enabled: allowed,
  });
  const showWelcome = status.data?.status === 200 && status.data.body.showWelcome === true;

  useEffect(() => {
    if (!allowed || !showWelcome || welcomeAlreadyShown(actor.orgId)) return;
    markWelcomeShown(actor.orgId);
    // Sign-up with a team picked from contacts lands on `/?invite=sms`: the team is in already, and
    // Home's "Text your team their invite" sheet is what opens instead of Add your team.
    if (new URLSearchParams(window.location.search).get('invite') === 'sms') return;
    if (shared) shared.open();
    else setOpen(true);
  }, [allowed, showWelcome, actor.orgId, shared]);

  if (!open) return null;
  const employeeLimit = status.data?.status === 200 ? status.data.body.employeeLimit : null;
  return (
    <Suspense fallback={null}>
      <LazyAddYourTeamModal
        open
        onClose={() => setOpen(false)}
        employeeLimit={employeeLimit ?? null}
      />
    </Suspense>
  );
}
